import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CardContent,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import apiService from "../../services/api";
import { DATE_FORMATS, API_CONFIG } from "../../constants";
import ImagePreviewCard from "../../components/common/ImagePreviewCard";
import DefaultImage from "../../assets/images/DefaultImage.png";
import useCodeCommon from "../../hooks/useCodeCommon";
import CustomLoading from "../../components/common/CustomLoading";
import { formatMobileNumber } from "../../utils/formatters";
import {
  parseFilePaths,
  isPdfFile,
  resolvePreviewSrc as resolvePreviewSrcUtil,
} from "../../utils/mediaUtils";
import { useAppSelector } from "../../hooks/useAuth";
import { THEME_PRIMARY } from "./bookingTheme";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, "/")}`;

// --- Image Helper Functions ---
const parseImageUrls = (imageString: string): string[] => {
  if (!imageString) return [];
  return imageString
    .split(",")
    .map((url) => url.trim())
    .filter((url) => url);
};

/** API sometimes returns duplicate rows per checklist id; keep last occurrence per id */
function dedupeChecklistMappingsByChecklistId(mappings: unknown): any[] {
  if (!Array.isArray(mappings)) return [];
  const byId = new Map<string, any>();
  for (const m of mappings) {
    if (m == null || typeof m !== "object") continue;
    const rec = m as { vehicleconditionchecklistid?: number | string };
    const id = rec.vehicleconditionchecklistid;
    if (id == null) continue;
    byId.set(String(id), m);
  }
  return Array.from(byId.values());
}

// Wrapper function that uses the local DefaultImage asset
const resolvePreviewSrc = (value: string | null | undefined): string => {
  return resolvePreviewSrcUtil(value, DefaultImage);
};

// --- StatusChip Component ---
const StatusChip = ({ value }: { value: string }) => {
  const getChipStyles = (status: string) => {
    switch (status) {
      case "Pending":
        return {
          backgroundColor: "rgba(255, 248, 220, 1)",
          color: "rgba(184, 134, 11, 1)",
          fontWeight: 600,
        };
      case "Approve":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Completed":
        return {
          backgroundColor: "rgba(212, 255, 199, 1)",
          color: "rgba(31, 113, 13, 1)",
          fontWeight: 600,
        };
      case "Rejected":
        return {
          backgroundColor: "rgba(255, 227, 227, 1)",
          color: "rgba(197, 0, 0, 1)",
          fontWeight: 600,
        };
      case "Ride In Progress":
        return {
          backgroundColor: "rgba(227, 242, 253, 1)",
          color: "rgba(25, 118, 210, 1)",
          fontWeight: 600,
        };
      default:
        return {
          backgroundColor: "rgba(245, 245, 245, 1)",
          color: "rgba(117, 117, 117, 1)",
          fontWeight: 600,
        };
    }
  };

  return <Chip label={value} size="small" sx={getChipStyles(value)} />;
};

const BookingDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const bookingId = searchParams.get("bookingId") || "";
  const savedFilters = location.state?.filters || {};
  const savedPage = location.state?.page || 0;
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelDialogError, setCancelDialogError] = useState<string | null>(
    null,
  );
  const cancelReasonRequiredMessage = "Cancellation reason is required.";
  const [extendDateOpen, setExtendDateOpen] = useState(false);
  const [extendDateLoading, setExtendDateLoading] = useState(false);
  const [extendedDate, setExtendedDate] = useState<Dayjs | null>(null);
  const [extensionReason, setExtensionReason] = useState("");
  const [extendDateError, setExtendDateError] = useState<string | null>(null);
  const [extensionHistory, setExtensionHistory] = useState<any[]>([]);
  const [extensionHistoryLoading, setExtensionHistoryLoading] = useState(false);
  /** Audit trail from GetAuditLogsByBookingId (booking activity / change history) */
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);

  // Get user role for permission check
  const { user } = useAppSelector((state: any) => state.auth);
  const canEditEndDate = user?.roleid && [1, 2, 7, 10].includes(user.roleid);
  const canCancelBooking = user?.roleid && [1, 2, 7, 10].includes(user.roleid);

  // Trip Status Options
  const { options: tripStatusOptions } = useCodeCommon("TripStatus");

  const getBookingStatus = (bookingData: any): string => {
    const status = bookingData?.tripstatus;

    // If tripstatus is null or undefined, treat it as status 1 (Pending)
    const statusId = status === null || status === undefined ? 1 : status;

    const statusOption = tripStatusOptions.find(
      (option) => Number.parseInt(option.codevalue) === statusId,
    );
    return statusOption ? statusOption.codedisplayname : "Pending";
  };

  // Helper function to resolve Rental Agreement PDF URL with Booking service base URL
  const resolveRentalAgreementPdfUrl = (
    pdfUrl: string | null | undefined,
  ): string => {
    if (!pdfUrl) return "";

    let url = String(pdfUrl).trim();

    if (url.startsWith("http://") || url.startsWith("https://")) {
      const filenameMatch = url.match(/([^\/]+\.pdf)$/i);
      if (filenameMatch) {
        const filename = filenameMatch[1];
        // Use API base URL instead of image base URL
        const API_BASE_URL = API_CONFIG.BASE_URL || "";
        const baseUrl = API_BASE_URL.replace(/\/?$/, "");
        return `${baseUrl}/Booking_Trip_ManagementService/PDF/AcceptedTerms/${filename}`;
      }
      // If we can't extract filename, return as-is
      return url;
    }

    // Handle relative paths
    // Extract filename from the path
    let filename = url;

    // Remove leading slashes
    filename = filename.replace(/^\/+/, "");

    // Extract just the filename if there's a path
    if (filename.includes("/")) {
      const parts = filename.split("/");
      filename = parts[parts.length - 1];
    }

    // Remove any CommonService/images prefix if present
    filename = filename.replace(/^.*\/PDF\/AcceptedTerms\//, "");
    filename = filename.replace(/^.*AcceptedTerms\//, "");

    // Use API base URL instead of image base URL
    const API_BASE_URL = API_CONFIG.BASE_URL || "";
    const baseUrl = API_BASE_URL.replace(/\/?$/, "");

    // Construct the path: Booking_Trip_ManagementService/PDF/AcceptedTerms/filename.pdf
    return `${baseUrl}/Booking_Trip_ManagementService/PDF/AcceptedTerms/${filename}`;
  };

  const handlePdfDownload = (pdfUrl: string) => {
    if (!pdfUrl) {
      console.error("PDF URL is not available");
      return;
    }

    try {
      // Use the rental agreement PDF resolver for booking PDFs
      const resolvedUrl = resolveRentalAgreementPdfUrl(pdfUrl);

      if (!resolvedUrl) {
        console.error("Could not resolve PDF URL");
        return;
      }

      // Open in new tab - works for both full URLs and relative paths
      window.open(resolvedUrl, "_blank");
    } catch (error) {
      console.error("Error opening PDF:", error);
    }
  };

  const handleOpenExtendDate = () => {
    if (bookingData?.planenddate) {
      const currentEndDate = dayjs(bookingData.planenddate);
      setExtendedDate(currentEndDate.add(1, "day")); // Set to tomorrow by default
      setExtensionReason("");
      setExtendDateError(null);
      setExtendDateOpen(true);
    }
  };

  const handleCloseExtendDate = () => {
    setExtendDateOpen(false);
    setExtendedDate(null);
    setExtensionReason("");
    setExtendDateError(null);
  };

  const handleSaveExtendedDate = async () => {
    if (!extendedDate) {
      setExtendDateError("Please select a new end date");
      return;
    }

    if (!extensionReason.trim()) {
      setExtendDateError("Please provide a reason for the extension");
      return;
    }

    const currentEndDate = bookingData?.planenddate;
    if (!currentEndDate) {
      setExtendDateError("Current end date is not available");
      return;
    }

    const newDateDayjs = extendedDate;
    const currentEndDateDayjs = dayjs(currentEndDate);

    if (!newDateDayjs.isAfter(currentEndDateDayjs)) {
      setExtendDateError("New end date must be after the current end date");
      return;
    }

    try {
      setExtendDateLoading(true);
      setExtendDateError(null);

      await apiService.savePlanExpiryDateExtension({
        bookingmasterid:
          bookingData?.bookingmasterid || parseInt(bookingId) || 0,
        previousenddate: currentEndDateDayjs.toISOString(),
        extendeddate: newDateDayjs.toISOString(),
        extensionreason: extensionReason.trim(),
      });

      // Refresh the booking details
      const response: any = await apiService.getBookingById(bookingId);
      const data = response?.data || response;
      setDetails(data);

      // Refresh extension history
      const bookingmasterid =
        bookingData?.bookingmasterid || parseInt(bookingId) || 0;
      await fetchExtensionHistory(bookingmasterid);

      handleCloseExtendDate();
    } catch (e: any) {
      console.error("Error extending plan expiry date", e);
      setExtendDateError(
        e?.message || "Failed to extend plan expiry date. Please try again.",
      );
    } finally {
      setExtendDateLoading(false);
    }
  };

  const fetchExtensionHistory = async (bookingmasterid?: number | string) => {
    const id = bookingmasterid || details?.[0]?.bookingmasterid || bookingId;
    if (!id) return;

    try {
      setExtensionHistoryLoading(true);
      const response: any = await apiService.getPlanExpiryDateExtensions(id);
      const data = response?.data || response || [];
      setExtensionHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error fetching extension history", e);
      setExtensionHistory([]);
    } finally {
      setExtensionHistoryLoading(false);
    }
  };

  const fetchAuditLogs = async (bookingmasterid?: number | string) => {
    const id = bookingmasterid || details?.[0]?.bookingmasterid || bookingId;
    if (!id) return;
    try {
      setAuditLogsLoading(true);
      const response: any = await apiService.getAuditLogsByBookingId(id);
      const data = response?.data ?? response?.result ?? response;
      const list = Array.isArray(data) ? data : [];
      // Newest first
      list.sort((a: any, b: any) => {
        const ta = new Date(a?.enteredon || 0).getTime();
        const tb = new Date(b?.enteredon || 0).getTime();
        return tb - ta;
      });
      setAuditLogs(list);
    } catch (e) {
      console.error("Error fetching audit logs", e);
      setAuditLogs([]);
    } finally {
      setAuditLogsLoading(false);
    }
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (!bookingId) return;
      try {
        setLoading(true);
        setError(null);
        const response: any = await apiService.getBookingById(bookingId);
        const data = response?.data || response;
        setDetails(data);
      } catch (e) {
        console.error("Error fetching booking details", e);
        setError("Failed to load booking details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [bookingId]);

  useEffect(() => {
    if (details && details[0]?.bookingmasterid) {
      fetchExtensionHistory();
      fetchAuditLogs();
    }
  }, [details]);

  const tripsHistory = useMemo(() => {
    const b = Array.isArray(details) && details.length > 0 ? details[0] : null;
    const raw = b?.trips_history ?? b?.tripsHistory;
    if (!Array.isArray(raw)) return [];
    return [...raw].sort((a, b) => {
      const ta = new Date(a?.createdon || 0).getTime();
      const tb = new Date(b?.createdon || 0).getTime();
      return tb - ta;
    });
  }, [details]);

  const couponActivityHistory = useMemo(() => {
    const b = Array.isArray(details) && details.length > 0 ? details[0] : null;
    const raw =
      b?.appliedPromoRespBO ?? b?.appliedPromoRespBo ?? b?.appliedpromorespbo;
    if (!Array.isArray(raw)) return [];
    return [...raw].sort((a, b) => {
      const ta = new Date(a?.applieddate ?? a?.appliedDate ?? 0).getTime();
      const tb = new Date(b?.applieddate ?? b?.appliedDate ?? 0).getTime();
      return tb - ta;
    });
  }, [details]);

  const getTripStatusLabel = (status: unknown): string => {
    const statusId =
      status === null || status === undefined ? 1 : Number(status);
    const opt = tripStatusOptions.find(
      (o) => Number.parseInt(String(o.codevalue), 10) === statusId,
    );
    return opt?.codedisplayname ?? "—";
  };

  if (loading) {
    return <CustomLoading message="Fetching Booking Details..." />;
  }

  const bookingData = details[0]; // Get first booking from array
  const tripStart = bookingData?.tripStart;
  const tripEnd = bookingData?.tripEnd;

  // Helper function to handle null, undefined, and empty strings
  const getValueOrNA = (value: any): string => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    const stringValue = String(value).trim();
    return stringValue === "" ? "N/A" : stringValue;
  };

  return (
    <Container fluid>
      {/* Basic Information Section */}
      <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "rgba(22, 67, 135, 1)",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              Basic Information
            </Typography>
            <Box display="flex" gap={2}>
              {bookingData?.termacceptedpdf && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handlePdfDownload(bookingData.termacceptedpdf)}
                  sx={{
                    borderColor: "rgba(22, 67, 135, 1)",
                    color: "rgba(22, 67, 135, 1)",
                    "&:hover": {
                      borderColor: "rgba(22, 67, 135, 0.8)",
                      backgroundColor: "rgba(22, 67, 135, 0.05)",
                    },
                  }}
                >
                  Download Rental Agreement
                </Button>
              )}
              {canCancelBooking &&
                (bookingData?.tripstatus === 5 ||
                  bookingData?.tripstatus === 1 ||
                  bookingData?.tripstatus == null ||
                  bookingData?.tripstatus === 2) && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setCancelReason("");
                      setCancelDialogError(null);
                      setCancelOpen(true);
                    }}
                    sx={{
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#b71c1c" },
                    }}
                  >
                    Cancel
                  </Button>
                )}
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Row className="g-3">
            {[
              {
                label: "Booking ID",
                value: String(bookingData?.bookingmasterid || bookingId),
              },
              { label: "User Name", value: getValueOrNA(bookingData?.name) },
              {
                label: "Phone Number",
                value: bookingData?.phonenumber
                  ? formatMobileNumber(bookingData.phonenumber)
                  : "N/A",
              },
              {
                label: "Alternate Mobile Number",
                value: bookingData?.alternatemobileno
                  ? formatMobileNumber(bookingData.alternatemobileno)
                  : "N/A",
              },
              { label: "Hub", value: getValueOrNA(bookingData?.hubname) },
              { label: "Plan", value: getValueOrNA(bookingData?.planname) },
              { label: "Status", value: getBookingStatus(bookingData) },
              { label: "Extra/KM", value: "N/A" },
              {
                label: "Booking Date",
                value: DATE_FORMATS.formatDate(
                  bookingData?.createdon || "-",
                ),
              },
              {
                label: "Scheduled Start Date",
                value: DATE_FORMATS.formatDate(
                  bookingData?.planstartdate || "-",
                ),
              },
              {
                label: "Scheduled End Date",
                value: DATE_FORMATS.formatDate(bookingData?.planenddate || ""),
                editable:
                  canEditEndDate &&
                  getBookingStatus(bookingData) !== "Completed",
              },
              {
                label: "Payments",
                value:
                  bookingData?.bookingamount != null
                    ? `₹${Number(bookingData.bookingamount).toLocaleString()}`
                    : "₹0",
              },
              {
                label: "Coupon Name",
                value: bookingData?.couponname ? bookingData.couponname : "N/A",
              },
              {
                label: "Discount Amount",
                value:
                  bookingData?.discountedamount != null
                    ? `₹${Number(bookingData.discountedamount).toLocaleString()}`
                    : "₹0",
              },
              { label: "Rental", value: getValueOrNA(bookingData?.planname) },
              {
                label: "Vehicle",
                value: getValueOrNA(bookingData?.vehiclename),
              },
              {
                label: "Vehicle Number",
                value: getValueOrNA(bookingData?.vehiclenum),
              },
              {
                label: "Registration Number",
                value: getValueOrNA(bookingData?.registrationnumber),
              },
              ...(String(bookingData?.remarks ?? "").trim()
                ? [
                  {
                    label: "Edit Remarks",
                    value: String(bookingData?.remarks).trim(),
                  },
                ]
                : []),
              ...(String(
                bookingData?.amount_adjustment_reason ??
                bookingData?.amountAdjustmentReason ??
                "",
              ).trim()
                ? [
                  {
                    label: "Onboarding Fee Adjustment Reason",
                    value: String(
                      bookingData?.amount_adjustment_reason ??
                      bookingData?.amountAdjustmentReason ??
                      "",
                    ).trim(),
                  },
                ]
                : []),
            ].map((item, index) => (
              <Col xs={12} md={6} lg={3} key={index}>
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontSize: 14 }}
                    >
                      {item.label}
                    </Typography>
                    {item.editable && (
                      <IconButton
                        size="small"
                        onClick={handleOpenExtendDate}
                        sx={{
                          color: "rgba(22, 67, 135, 1)",
                          "&:hover": {
                            backgroundColor: "rgba(22, 67, 135, 0.1)",
                          },
                          padding: "2px",
                          marginLeft: "-4px",
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {item.label === "Status" ? (
                    <StatusChip value={item.value} />
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ fontSize: 16, fontWeight: 600 }}
                    >
                      {item.value}
                    </Typography>
                  )}
                </Box>
              </Col>
            ))}
          </Row>
        </CardContent>
      </Paper>

      {/* Booking activity / audit history (API: GetAuditLogsByBookingId) */}
      <Accordion
        defaultExpanded={false}
        className="mb-4"
        sx={{
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "rgba(22, 67, 135, 1)" }} />}
          sx={{
            px: 2.5,
            py: 1.5,
            "&:hover": {
              backgroundColor: "rgba(22, 67, 135, 0.02)",
            },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ width: "100%" }}
          >
            <HistoryIcon sx={{ color: "rgba(22, 67, 135, 1)", fontSize: 26 }} />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(22, 67, 135, 1)",
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                Booking activity history
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                Audit trail of changes and events for this booking
                {auditLogs.length > 0 &&
                  ` · ${auditLogs.length} ${auditLogs.length === 1 ? "entry" : "entries"}`}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
          {auditLogsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading activity history...
              </Typography>
            </Box>
          ) : auditLogs.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              No activity records found for this booking.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {auditLogs.map((log, index) => (
                <Paper
                  key={log.auditid ?? `${index}-${log.enteredon}`}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      borderColor: "rgba(22, 67, 135, 0.25)",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    gap={1}
                    mb={1.5}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        label={
                          Number(log.categoryid) === 1
                            ? "Booking / plan"
                            : "Update"
                        }
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          backgroundColor:
                            Number(log.categoryid) === 1
                              ? "rgba(227, 242, 253, 1)"
                              : "rgba(245, 245, 245, 1)",
                          color: "rgba(22, 67, 135, 1)",
                        }}
                      />
                      {log.planname ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Plan: {log.planname}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: 12 }}
                    >
                      {DATE_FORMATS.formatDateTime(log.enteredon || "")}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      lineHeight: 1.65,
                      fontSize: 14,
                    }}
                  >
                    {log.description || "—"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block", mt: 1 }}
                  >
                    User ID: {log.userid != null ? String(log.userid) : "N/A"} ·
                    User Name:{" "}
                    {log.actionusername != null
                      ? String(log.actionusername)
                      : "N/A"}
                    {log.auditid != null ? ` · Audit #${log.auditid}` : ""}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Coupon activity history (from booking details appliedPromoRespBO) */}
      <Accordion
        defaultExpanded={false}
        className="mb-4"
        sx={{
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e0e0e0",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "rgba(22, 67, 135, 1)" }} />}
          sx={{
            px: 2.5,
            py: 1.5,
            "&:hover": {
              backgroundColor: "rgba(22, 67, 135, 0.02)",
            },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ width: "100%" }}
          >
            <HistoryIcon sx={{ color: "rgba(22, 67, 135, 1)", fontSize: 26 }} />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(22, 67, 135, 1)",
                  fontSize: 18,
                  fontWeight: 600,
                }}
              >
                Coupon activity history
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                Promos and discounts applied to this booking
                {couponActivityHistory.length > 0 &&
                  ` · ${couponActivityHistory.length} ${couponActivityHistory.length === 1 ? "entry" : "entries"}`}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
          {couponActivityHistory.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
              No coupon activity records found for this booking.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {couponActivityHistory.map((row: any, index: number) => {
                const couponName = String(
                  row?.couponname ?? row?.couponName ?? "",
                ).trim();
                const couponId = row?.couponid ?? row?.couponId;
                const discountRaw =
                  row?.discountedamount ?? row?.discountedAmount;
                const discountNum = Number(discountRaw);
                const discountLabel = Number.isFinite(discountNum)
                  ? `₹${discountNum.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                  : "—";
                const appliedAt = row?.applieddate ?? row?.appliedDate ?? "";
                return (
                  <Paper
                    key={`${couponId ?? "c"}-${appliedAt}-${index}`}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        borderColor: "rgba(22, 67, 135, 0.25)",
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      flexWrap="wrap"
                      gap={1}
                      mb={1.5}
                    >
                      <Chip
                        size="small"
                        label="Coupon applied"
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          backgroundColor: "rgba(227, 242, 253, 1)",
                          color: "rgba(22, 67, 135, 1)",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.65,
                        fontSize: 14,
                      }}
                    >
                      <b>Applied Date:</b>{" "}
                      {appliedAt
                        ? DATE_FORMATS.formatDateTime(String(appliedAt))
                        : "—"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.65,
                        fontSize: 14,
                      }}
                    >
                      <b>Coupon Name:</b> {couponName || "—"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        lineHeight: 1.65,
                        fontSize: 14,
                        mt: 0.5,
                      }}
                    >
                      <b>Discount Amount:</b> {discountLabel}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Plan Expiry Date Extension History Section */}
      {extensionHistory.length > 0 && (
        <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(22, 67, 135, 1)",
                fontSize: 18,
                fontWeight: 600,
                mb: 2,
              }}
            >
              Plan Expiry Date Extension History
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {extensionHistoryLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={4}
              >
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading extension history...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {extensionHistory
                  .slice()
                  .reverse()
                  .map((extension, index) => (
                    <Paper
                      key={extension.expiryextensionid || index}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          borderColor: "rgba(22, 67, 135, 0.3)",
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: "rgba(22, 67, 135, 1)",
                              fontWeight: 600,
                              fontSize: 14,
                            }}
                          >
                            Extension #{extensionHistory.length - index}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: 12,
                          }}
                        >
                          {DATE_FORMATS.formatDateTime(
                            extension.createdon || "",
                          )}
                        </Typography>
                      </Box>

                      <Row className="g-3">
                        <Col xs={12} md={6}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontSize: 12,
                                mb: 0.5,
                              }}
                            >
                              Previous End Date
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontSize: 14, fontWeight: 600 }}
                            >
                              {DATE_FORMATS.formatDate(
                                extension.previousenddate || "",
                              )}
                            </Typography>
                          </Box>
                        </Col>
                        <Col xs={12} md={6}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontSize: 12,
                                mb: 0.5,
                              }}
                            >
                              Extended To
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: "rgba(31, 113, 13, 1)",
                              }}
                            >
                              {DATE_FORMATS.formatDate(
                                extension.extendeddate || "",
                              )}
                            </Typography>
                          </Box>
                        </Col>
                        <Col xs={12}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontSize: 12,
                                mb: 0.5,
                              }}
                            >
                              Extension Reason
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: 14,
                                color: "text.primary",
                                fontStyle: extension.extensionreason
                                  ? "normal"
                                  : "italic",
                              }}
                            >
                              {extension.extensionreason ||
                                "No reason provided"}
                            </Typography>
                          </Box>
                        </Col>
                        <Col xs={12}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary", fontSize: 12 }}
                            >
                              Extended by:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: 13, fontWeight: 500 }}
                            >
                              {extension.name || "N/A"}
                            </Typography>
                          </Box>
                        </Col>
                      </Row>
                    </Paper>
                  ))}
              </Box>
            )}
          </CardContent>
        </Paper>
      )}

      {/* Address Documents Section */}
      {bookingData?.addressdocument &&
        parseFilePaths(bookingData.addressdocument).length > 0 && (
          <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(22, 67, 135, 1)",
                  fontSize: 18,
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Address Documents
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Row className="g-3">
                {parseFilePaths(bookingData.addressdocument).map(
                  (filePath, index) => (
                    <Col xs={12} md={6} lg={4} key={index}>
                      <Box
                        sx={{
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          backgroundColor: "#fafafa",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            color: "rgba(22, 67, 135, 1)",
                            mb: 2,
                            textAlign: "center",
                          }}
                        >
                          Address Document{" "}
                          {parseFilePaths(bookingData.addressdocument).length >
                            1
                            ? `(${index + 1})`
                            : ""}
                        </Typography>
                        {isPdfFile(filePath) ? (
                          <>
                            <Box
                              sx={{
                                width: "120px",
                                height: "120px",
                                backgroundColor: "rgba(22, 67, 135, 0.1)",
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                                border: "2px solid rgba(22, 67, 135, 0.2)",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "2rem",
                                  color: "rgba(22, 67, 135, 0.7)",
                                }}
                              >
                                📄
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: "rgba(22, 67, 135, 1)",
                                "&:hover": {
                                  backgroundColor: "rgba(22, 67, 135, 0.8)",
                                },
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                              }}
                              onClick={() =>
                                window.open(
                                  resolvePreviewSrc(filePath),
                                  "_blank",
                                )
                              }
                            >
                              View PDF
                            </Button>
                          </>
                        ) : (
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: 2,
                              overflow: "hidden",
                              border: "2px solid #e0e0e0",
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "rgba(22, 67, 135, 0.5)",
                              },
                            }}
                          >
                            <img
                              src={resolvePreviewSrc(filePath)}
                              alt={`Address Document ${index + 1}`}
                              style={{
                                width: "180px",
                                height: "180px",
                                objectFit: "cover",
                                display: "block",
                              }}
                              onClick={() =>
                                window.open(
                                  resolvePreviewSrc(filePath),
                                  "_blank",
                                )
                              }
                            />
                          </Box>
                        )}
                      </Box>
                    </Col>
                  ),
                )}
              </Row>
            </CardContent>
          </Paper>
        )}

      {/* Cancel Confirmation */}
      <Dialog
        open={cancelOpen}
        onClose={() => {
          if (cancelLoading) return;
          setCancelOpen(false);
          setCancelReason("");
          setCancelDialogError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to cancel booking #
            {String(bookingData?.bookingmasterid || bookingId)}?
          </DialogContentText>
          <TextField
            label="Cancellation reason"
            placeholder="Enter a reason for cancelling this booking"
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              if (cancelDialogError) setCancelDialogError(null);
            }}
            multiline
            minRows={3}
            fullWidth
            required
            disabled={cancelLoading}
            error={cancelDialogError === cancelReasonRequiredMessage}
            helperText={
              cancelDialogError === cancelReasonRequiredMessage
                ? cancelReasonRequiredMessage
                : undefined
            }
          />
          {cancelLoading && (
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <CircularProgress size={18} />
              <Typography variant="body2">Cancelling...</Typography>
            </Box>
          )}
          {cancelDialogError &&
            cancelDialogError !== cancelReasonRequiredMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {cancelDialogError}
              </Alert>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelOpen(false);
              setCancelReason("");
              setCancelDialogError(null);
            }}
            disabled={cancelLoading}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelLoading}
            onClick={async () => {
              const trimmed = cancelReason.trim();
              if (trimmed === "") {
                setCancelDialogError(cancelReasonRequiredMessage);
                return;
              }
              try {
                setCancelLoading(true);
                setCancelDialogError(null);
                const id = String(bookingData?.bookingmasterid || bookingId);
                await apiService.cancelBooking(id, trimmed);
                setCancelOpen(false);
                setCancelReason("");
                navigate("/booking-management", {
                  state: {
                    filters: savedFilters,
                    page: savedPage,
                    shouldRefetch: true,
                  },
                });
              } catch (e: any) {
                setCancelDialogError(e?.message || "Failed to cancel booking");
              } finally {
                setCancelLoading(false);
              }
            }}
          >
            Yes, cancel booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend End Date Dialog */}
      <Dialog
        open={extendDateOpen}
        onClose={handleCloseExtendDate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Extend Plan End Date</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Warning Alert */}
            <Alert
              severity="warning"
              icon={false}
              sx={{
                backgroundColor: "#fff3cd",
                border: "2px solid #ffc107",
                borderRadius: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#856404",
                    fontWeight: 700,
                    fontSize: 14,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  ⚠️ Important Warning
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}
                >
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      color: "#856404",
                      fontSize: 13,
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    This action is <strong>not reversible</strong> - once
                    extended, you cannot revert to the previous date
                  </Typography>
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      color: "#856404",
                      fontSize: 13,
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    You <strong>cannot select past dates</strong> - only future
                    dates are allowed
                  </Typography>
                  <Typography
                    component="li"
                    variant="body2"
                    sx={{
                      color: "#856404",
                      fontSize: 13,
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    You <strong>cannot change the previous date</strong> after
                    applying this extension
                  </Typography>
                </Box>
              </Box>
            </Alert>

            <Box>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Current End Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {DATE_FORMATS.formatDate(bookingData?.planenddate || "")}
              </Typography>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="New End Date"
                value={extendedDate}
                onChange={(newValue) => setExtendedDate(newValue)}
                minDate={
                  bookingData?.planenddate
                    ? dayjs(bookingData.planenddate).add(1, "day")
                    : dayjs().add(1, "day")
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    error: !!extendDateError && !extendedDate,
                    helperText:
                      extendDateError && !extendedDate
                        ? extendDateError
                        : "Select a future date",
                  },
                }}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>

            <TextField
              label="Extension Reason"
              value={extensionReason}
              onChange={(e) => setExtensionReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
              required
              error={!!extendDateError && !extensionReason.trim()}
              helperText={
                extendDateError && !extensionReason.trim()
                  ? extendDateError
                  : "Please provide a reason for extending the end date"
              }
              placeholder="Enter the reason for extending the plan end date..."
            />

            {extendDateError && extendedDate && extensionReason.trim() && (
              <Alert severity="error">{extendDateError}</Alert>
            )}

            {extendDateLoading && (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">Saving...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtendDate} disabled={extendDateLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveExtendedDate}
            disabled={
              extendDateLoading || !extendedDate || !extensionReason.trim()
            }
            sx={{
              backgroundColor: "rgba(22, 67, 135, 1)",
              "&:hover": {
                backgroundColor: "rgba(22, 67, 135, 0.8)",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attachments Section */}
      <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(22, 67, 135, 1)",
              fontSize: 18,
              fontWeight: 600,
              mb: 2,
            }}
          >
            Attachments
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Row className="g-3">
            {/* Pre-trip */}
            <Col xs={12} md={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "text.secondary", fontWeight: 600 }}
                >
                  Pre-trip
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {/* Vehicle Photos */}
                  {(() => {
                    const urls = parseImageUrls(tripStart?.vehiclephotos || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`pre-trip-vehicle-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`Vehicle Photo ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}

                  {/* Battery Photos */}
                  {(() => {
                    const urls = parseImageUrls(tripStart?.batteryphotos || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`pre-trip-battery-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`Battery Photo ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}

                  {/* Charger Photos */}
                  {(() => {
                    const urls = parseImageUrls(tripStart?.chargerphotos || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`pre-trip-charger-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`Charger Photo ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}

                  {/* E-sign */}
                  {(() => {
                    const urls = parseImageUrls(tripStart?.e_signature || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`pre-trip-e-sign-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`E-Signature ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}
                </Box>
              </Box>
            </Col>

            {/* Post-trip */}
            <Col xs={12} md={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: "text.secondary", fontWeight: 600 }}
                >
                  Post-trip
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {/* Vehicle Photos */}
                  {(() => {
                    const urls = parseImageUrls(tripEnd?.vehiclephotos || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`post-trip-vehicle-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`Vehicle Photo ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}

                  {/* Battery Photos */}
                  {(() => {
                    const urls = parseImageUrls(tripEnd?.batteryphotos || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`post-trip-battery-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`Battery Photo ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}

                  {/* Charger Photos */}
                  {(() => {
                    const urls = parseImageUrls(tripEnd?.chargerphotos || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`post-trip-charger-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`Charger Photo ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}

                  {/* E-sign */}
                  {(() => {
                    const urls = parseImageUrls(tripEnd?.e_signature || "");
                    return urls.map((url, idx) => (
                      <Box
                        key={`post-trip-e-sign-${idx}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ImagePreviewCard
                          title={`E-Signature ${idx + 1}`}
                          value={url}
                          resolveSrc={resolvePreviewSrc}
                          size={150}
                          defaultImage={DefaultImage}
                        />
                      </Box>
                    ));
                  })()}
                </Box>
              </Box>
            </Col>
          </Row>
        </CardContent>
      </Paper>

      {/* Vehicle Checklist Section */}
      {(tripStart?.vehicleconditionchecklistmappings ||
        tripEnd?.vehicleconditionchecklistmappings) && (
          <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(22, 67, 135, 1)",
                  fontSize: 18,
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Vehicle Checklist
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Row className="g-3">
                {/* Trip Start Checklist */}
                {tripStart?.vehicleconditionchecklistmappings && (
                  <Col xs={12} md={6}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "text.secondary", fontWeight: 600 }}
                      >
                        Trip Start Checklist
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(auto-fill, minmax(180px, 1fr))",
                          },
                          gap: 0.5,
                        }}
                      >
                        {dedupeChecklistMappingsByChecklistId(
                          tripStart.vehicleconditionchecklistmappings,
                        ).map((item: any, idx: number) => {
                          const checked =
                            item.vehicleconditioncheckedstatus === true;
                          return (
                            <Box
                              key={item.vehicleconditionchecklistid ?? idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Chip
                                label={checked ? "✓" : "✗"}
                                size="small"
                                sx={{
                                  backgroundColor: checked
                                    ? "#D4FFC7"
                                    : "#FFE3E3",
                                  color: checked ? "#1F710D" : "#C50000",
                                  fontWeight: "bold",
                                  minWidth: 30,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {item.checklistname || "N/A"}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Col>
                )}

                {/* Trip End Checklist */}
                {tripEnd?.vehicleconditionchecklistmappings && (
                  <Col xs={12} md={6}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: "text.secondary", fontWeight: 600 }}
                      >
                        Trip End Checklist
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(auto-fill, minmax(180px, 1fr))",
                          },
                          gap: 0.5,
                        }}
                      >
                        {dedupeChecklistMappingsByChecklistId(
                          tripEnd.vehicleconditionchecklistmappings,
                        ).map((item: any, idx: number) => {
                          const checked =
                            item.vehicleconditioncheckedstatus === true;
                          return (
                            <Box
                              key={item.vehicleconditionchecklistid ?? idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Chip
                                label={checked ? "✓" : "✗"}
                                size="small"
                                sx={{
                                  backgroundColor: checked
                                    ? "#D4FFC7"
                                    : "#FFE3E3",
                                  color: checked ? "#1F710D" : "#C50000",
                                  fontWeight: "bold",
                                  minWidth: 30,
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {item.checklistname || "N/A"}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Col>
                )}
              </Row>
            </CardContent>
          </Paper>
        )}

      {/* Trip history: expandable rows (trips_history from GetTripByBookingId / booking details) */}
      {tripsHistory.length > 0 && (
        <Paper className="mb-4 shadow-sm border-0" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                color: THEME_PRIMARY,
                fontSize: 18,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              Trip History
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Expand a row to view that trip&apos;s checklist and attachments
              (same layout as trip checklist in booking flow).
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {tripsHistory.map((trip: any, histIdx: number) => (
                <Accordion
                  key={trip.tripmasterid ?? `hist-${histIdx}`}
                  elevation={0}
                  disableGutters
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px !important",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: THEME_PRIMARY }} />
                    }
                  >
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                      gap={1.5}
                      sx={{ width: "100%", pr: 1 }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: THEME_PRIMARY }}
                      >
                        Trip #{trip.tripmasterid ?? histIdx + 1}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {DATE_FORMATS.formatDateTime(trip.createdon || "")}
                      </Typography>
                      {trip.receiveragentname ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          · Receiver: {trip.receiveragentname}
                        </Typography>
                      ) : null}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      pt: 0,
                      px: 2,
                      pb: 2,
                      bgcolor: "rgba(22, 67, 135, 0.03)",
                    }}
                  >
                    {trip.vehicleconditionchecklistmappings &&
                      trip.vehicleconditionchecklistmappings.length > 0 ? (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
                        >
                          Vehicle checklist
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, 1fr)",
                              md: "repeat(auto-fill, minmax(180px, 1fr))",
                            },
                            gap: 0.5,
                            mb: 2,
                          }}
                        >
                          {dedupeChecklistMappingsByChecklistId(
                            trip.vehicleconditionchecklistmappings,
                          ).map((item: any, idx: number) => {
                            const checked =
                              item.vehicleconditioncheckedstatus === true;
                            return (
                              <Box
                                key={`${trip.tripmasterid}-${item.vehicleconditionchecklistid}-${idx}`}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Chip
                                  label={checked ? "✓" : "✗"}
                                  size="small"
                                  sx={{
                                    backgroundColor: checked
                                      ? "#D4FFC7"
                                      : "#FFE3E3",
                                    color: checked ? "#1F710D" : "#C50000",
                                    fontWeight: "bold",
                                    minWidth: 30,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  {item.checklistname || "N/A"}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        No checklist recorded for this trip.
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: THEME_PRIMARY, fontWeight: 600, mb: 1 }}
                    >
                      Attachments
                    </Typography>
                    <Row className="g-3">
                      <Col xs={12} md={4}>
                        {parseImageUrls(trip.vehiclephotos || "").length > 0 ? (
                          <Box
                            sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                          >
                            {parseImageUrls(trip.vehiclephotos || "").map(
                              (url, idx) => (
                                <ImagePreviewCard
                                  key={`hist-v-${trip.tripmasterid}-${idx}`}
                                  title={`Vehicle ${idx + 1}`}
                                  value={url}
                                  resolveSrc={resolvePreviewSrc}
                                  size={200}
                                  defaultImage={DefaultImage}
                                />
                              ),
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            No vehicle images
                          </Typography>
                        )}
                      </Col>
                      <Col xs={12} md={4}>
                        {parseImageUrls(trip.batteryphotos || "").length > 0 ? (
                          <Box
                            sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                          >
                            {parseImageUrls(trip.batteryphotos || "").map(
                              (url, idx) => (
                                <ImagePreviewCard
                                  key={`hist-b-${trip.tripmasterid}-${idx}`}
                                  title={`Battery ${idx + 1}`}
                                  value={url}
                                  resolveSrc={resolvePreviewSrc}
                                  size={200}
                                  defaultImage={DefaultImage}
                                />
                              ),
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            No battery images
                          </Typography>
                        )}
                      </Col>
                      <Col xs={12} md={4}>
                        {parseImageUrls(trip.chargerphotos || "").length > 0 ? (
                          <Box
                            sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                          >
                            {parseImageUrls(trip.chargerphotos || "").map(
                              (url, idx) => (
                                <ImagePreviewCard
                                  key={`hist-c-${trip.tripmasterid}-${idx}`}
                                  title={`Charger ${idx + 1}`}
                                  value={url}
                                  resolveSrc={resolvePreviewSrc}
                                  size={200}
                                  defaultImage={DefaultImage}
                                />
                              ),
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            No charger images
                          </Typography>
                        )}
                      </Col>
                      <Col xs={12} md={4}>
                        {parseImageUrls(trip.e_signature || "").length > 0 ? (
                          <Box
                            sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                          >
                            {parseImageUrls(trip.e_signature || "").map(
                              (url, idx) => (
                                <ImagePreviewCard
                                  key={`hist-e-${trip.tripmasterid}-${idx}`}
                                  title={`E-signature ${idx + 1}`}
                                  value={url}
                                  resolveSrc={resolvePreviewSrc}
                                  size={200}
                                  defaultImage={DefaultImage}
                                />
                              ),
                            )}
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            No e-signature
                          </Typography>
                        )}
                      </Col>
                    </Row>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </CardContent>
        </Paper>
      )}
    </Container>
  );
};

export default BookingDetailsPage;
